import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Delivery, DeliveryList, Member, SettingsResponse, page } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { SignaturePad, UploadWithProgress } from '../../components/phase3';
import type { SignatureValue } from '../../components/phase3';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { SiteSelect, useActiveGroups } from './api';

const MemberPage = page(Member);
const PAGE_SIZE = 20;
type Status = 'waiting' | 'collected' | 'returned' | 'cancelled' | 'all';
const STATUSES: Status[] = ['waiting', 'collected', 'returned', 'cancelled', 'all'];
const TONE: Record<Delivery['status'], 'good' | 'warn' | 'neutral' | 'bad'> = { waiting: 'warn', collected: 'good', returned: 'neutral', cancelled: 'bad' };
const listKey = ['deliveries'] as const;

function LogForm({ siteId, onDone }: { siteId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const canPickMember = useCan('groups.view_members');
  const groups = useActiveGroups();
  const repeatGroups = (groups.data ?? []).filter((g) => g.kind === 'repeat');
  const [groupId, setGroupId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberId, setMemberId] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [carrier, setCarrier] = useState('');
  const [parcels, setParcels] = useState(1);
  const [location, setLocation] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [notify, setNotify] = useState(true);
  const [photoUploadId, setPhotoUploadId] = useState<string | null>(null);
  const effectiveGroup = groupId || repeatGroups[0]?.id || '';
  const members = useQuery({
    queryKey: ['deliveries', 'members', effectiveGroup, memberSearch],
    enabled: canPickMember && effectiveGroup !== '',
    queryFn: () => api.get('/members', { query: { groupId: effectiveGroup, status: 'current', search: memberSearch, pageSize: 20 }, schema: MemberPage }),
  });
  const create = useMutation({
    mutationFn: () =>
      api.post('/deliveries', {
        body: {
          siteId,
          recipientMemberId: memberId === '' ? null : memberId,
          recipientName: memberId === '' ? recipientName : '',
          carrier,
          reference,
          parcels,
          notes,
          location,
          photoUploadId,
          notify,
        },
        schema: Delivery,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: listKey });
      onDone();
    },
  });
  const nobody = memberId === '' && recipientName.trim() === '';
  return (
    <Card title={t('siteF.deliveries.log')} className="mb-6">
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="sm:col-span-2">
          <ErrorLine error={create.error} />
        </div>
        {canPickMember && repeatGroups.length > 0 ? (
          <>
            <SelectField label={t('siteF.deliveries.memberGroup')} value={effectiveGroup} onChange={(e) => { setGroupId(e.target.value); setMemberId(''); }}>
              {repeatGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </SelectField>
            <TextField label={t('siteF.deliveries.memberSearch')} type="search" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />
            <SelectField label={t('siteF.deliveries.member')} value={memberId} onChange={(e) => setMemberId(e.target.value)} className="sm:col-span-2">
              <option value="">{t('siteF.deliveries.noMember')}</option>
              {(members.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName}
                </option>
              ))}
            </SelectField>
          </>
        ) : null}
        {memberId === '' ? (
          <TextField label={t('siteF.deliveries.recipientName')} hint={t('siteF.deliveries.recipientNameHint')} maxLength={120} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />
        ) : null}
        <TextField label={t('siteF.deliveries.carrier')} maxLength={80} value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        <TextField label={t('siteF.deliveries.parcels')} type="number" min={1} max={100} value={String(parcels)} onChange={(e) => setParcels(Math.min(100, Math.max(1, Math.floor(Number(e.target.value) || 1))))} />
        <TextField label={t('siteF.deliveries.location')} hint={t('siteF.deliveries.locationHint')} maxLength={120} value={location} onChange={(e) => setLocation(e.target.value)} />
        <TextField label={t('siteF.deliveries.reference')} maxLength={120} value={reference} onChange={(e) => setReference(e.target.value)} />
        <TextField label={t('siteF.deliveries.notes')} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} className="sm:col-span-2" />
        <div className="sm:col-span-2">
          <UploadWithProgress
            label={t('siteF.deliveries.photo')}
            hint={t('siteF.deliveries.photoHint')}
            url="/api/v1/deliveries/uploads"
            fields={{ siteId }}
            accept={['image/jpeg']}
            maxBytes={1024 * 1024}
            onDone={(res) => setPhotoUploadId((res as { uploadId?: string } | null)?.uploadId ?? null)}
          />
        </div>
        <CheckboxField label={t('siteF.deliveries.notify')} checked={notify} onChange={(e) => setNotify(e.target.checked)} className="sm:col-span-2" />
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={create.isPending || nobody}>
            {t('siteF.deliveries.logSubmit')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

function CollectForm({ delivery, signatureRequired, onDone }: { delivery: Delivery; signatureRequired: boolean; onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(delivery.recipientName);
  const [signature, setSignature] = useState<SignatureValue | null>(null);
  const collect = useMutation({
    mutationFn: () => api.post(`/deliveries/${delivery.id}/collect`, { body: { collectedByName: name, signature }, schema: Delivery }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: listKey });
      onDone();
    },
  });
  return (
    <form
      className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        collect.mutate();
      }}
    >
      <ErrorLine error={collect.error} />
      <TextField label={t('siteF.deliveries.collectedBy')} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
      <SignaturePad label={signatureRequired ? t('siteF.deliveries.signature') : t('siteF.deliveries.signatureOptional')} onChange={setSignature} />
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={collect.isPending || name.trim() === '' || (signatureRequired && signature === null)}>
          {t('siteF.deliveries.collectSubmit')}
        </Button>
        <Button onClick={onDone}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}

function Row({ delivery, canManage, signatureRequired }: { delivery: Delivery; canManage: boolean; signatureRequired: boolean }) {
  const qc = useQueryClient();
  const [collecting, setCollecting] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: listKey });
  const act = useMutation({ mutationFn: (what: 'return' | 'cancel') => api.post(`/deliveries/${delivery.id}/${what}`, { schema: Delivery }), onSuccess: refresh });
  const notify = useMutation({ mutationFn: () => api.post(`/deliveries/${delivery.id}/notify`) });
  const waiting = delivery.status === 'waiting';
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(delivery.loggedAt)}</td>
      <td className="px-4 py-3">
        <p className="font-medium">{delivery.recipientName}</p>
        {delivery.notes ? <p className="text-xs text-slate-600">{delivery.notes}</p> : null}
      </td>
      <td className="px-4 py-3">
        {delivery.carrier}
        {delivery.reference ? <span className="block text-xs text-slate-600">{delivery.reference}</span> : null}
      </td>
      <td className="px-4 py-3 tabular-nums">{delivery.parcels}</td>
      <td className="px-4 py-3">{delivery.location}</td>
      <td className="px-4 py-3">
        <Badge tone={TONE[delivery.status]}>{t(`siteF.deliveries.status.${delivery.status}`)}</Badge>
        {delivery.collectedAt ? (
          <span className="mt-1 block text-xs text-slate-600">{t('siteF.deliveries.collectedLine', { name: delivery.collectedByName ?? '', when: formatDateTime(delivery.collectedAt) })}</span>
        ) : null}
        {waiting && delivery.remindersSent > 0 ? <span className="mt-1 block text-xs text-slate-600">{t('siteF.deliveries.reminders', { n: delivery.remindersSent })}</span> : null}
      </td>
      <td className="px-4 py-3">
        {canManage && waiting ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setCollecting(true)} disabled={collecting} aria-label={t('siteF.deliveries.collectFor', { name: delivery.recipientName })}>
              {t('siteF.deliveries.collect')}
            </Button>
            <Button onClick={() => act.mutate('return')} disabled={act.isPending} aria-label={t('siteF.deliveries.returnFor', { name: delivery.recipientName })}>
              {t('siteF.deliveries.return')}
            </Button>
            <Button onClick={() => notify.mutate()} disabled={notify.isPending || delivery.recipientMemberId === null}>
              {t('siteF.deliveries.notifyAgain')}
            </Button>
            <ConfirmButton label={t('siteF.deliveries.cancel')} pending={act.isPending} onConfirm={() => act.mutate('cancel')} />
          </div>
        ) : null}
        {notify.isSuccess ? <p role="status" className="mt-1 text-xs text-green-800">{t('siteF.deliveries.notified')}</p> : null}
        <ErrorLine error={act.error ?? notify.error} />
        {collecting ? <CollectForm delivery={delivery} signatureRequired={signatureRequired} onDone={() => setCollecting(false)} /> : null}
      </td>
    </tr>
  );
}

/** Reception page "Deliveries" (deliveries.md section 5): log a parcel, see what is waiting, record collection or return. */
export function DeliveriesPage() {
  const canManage = useCan('deliveries.manage');
  const list = useListState();
  const [siteId, setSiteId] = useState('');
  const [status, setStatus] = useState<Status>('waiting');
  const [logging, setLogging] = useState(false);
  const siteSettings = useQuery({
    queryKey: ['deliveries', 'site-settings', siteId],
    enabled: siteId !== '',
    queryFn: () => api.get(`/sites/${siteId}/settings/deliveries`, { schema: SettingsResponse }),
  });
  const signatureRequired = siteSettings.data?.values['requireSignatureOnCollect'] === true;
  const query = { siteId, status, q: list.search, page: list.page, pageSize: PAGE_SIZE };
  const q = useQuery({
    queryKey: [...listKey, query],
    enabled: siteId !== '',
    queryFn: () => api.get('/deliveries', { query, schema: DeliveryList }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <PageHeader
        title={t('siteF.deliveries.title')}
        description={t('siteF.deliveries.description')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={siteId === '' || logging} onClick={() => setLogging(true)}>
              {t('siteF.deliveries.log')}
            </Button>
          ) : null
        }
      />
      {logging ? <LogForm siteId={siteId} onDone={() => setLogging(false)} /> : null}
      <FilterBar columns="sm:grid-cols-3">
        <SiteSelect
          value={siteId}
          onChange={(id) => {
            setSiteId(id);
            list.resetPage();
          }}
        />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as Status);
            list.resetPage();
          }}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? t('common.all') : t(`siteF.deliveries.status.${s}`)}
            </option>
          ))}
        </SelectField>
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
      </FilterBar>
      {signatureRequired && canManage ? <Alert kind="info">{t('siteF.deliveries.signatureNote')}</Alert> : null}
      {siteId === '' || q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('siteF.deliveries.title')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('siteF.deliveries.col.logged')}</Th>
                <Th>{t('siteF.deliveries.col.recipient')}</Th>
                <Th>{t('siteF.deliveries.carrier')}</Th>
                <Th>{t('siteF.deliveries.parcels')}</Th>
                <Th>{t('siteF.deliveries.location')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={7} /> : null}
              {q.data.items.map((d) => (
                <Row key={d.id} delivery={d} canManage={canManage} signatureRequired={signatureRequired} />
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
